import * as React from 'react';
import { useContext, useRef } from 'react';
import classes from './Settings.module.scss';
import { SettingsMenu } from './SettingsMenu';
import { Route, Routes, useMatch } from 'react-router-dom';
import { Account } from './Account';
import { EmailNotification } from './EmailNotification';
import classNames from 'classnames';
import { TeamManagement } from './TeamManagement';
import { Workspaces } from './Workspaces';
import { Publishing } from './Publishing';
import { Profiles } from './Profiles';
import { useDimensions } from '../../hooks/useDimensions';
import { TopNavContext } from '../../providers/TopNavProvider';
import { LinkTracking } from './LinkTracking';
import { Pause } from './Pause';
import { useTopNavClasses } from '../../hooks/useTopNavClasees';
import { BrowserExtension } from './BrowserExtension';
import { MobileApp } from './MobileApp';
import { Subscription } from './Subscription';
import { Affiliate } from './Affiliate';
import { BillingHistory } from './BillingHistory';
import { Invoice } from './Invoice';
import { Admin } from './Admin';
import { NotFoundRedirect } from '../App/NotFoundRedirect';
import { useUserRole } from '../../hooks/useUserRole';
import { UserOrganizationRole } from '../../models/graphql';
import { BusinessDetails } from './BusinessDetails';
import { WithChildren } from '../../models/children-props';
import { useSelectedOrganization } from '../../hooks/useSelectedOrganization';

const IframeElement = (props: WithChildren): JSX.Element => (
  <div className={classNames(classes.IframeContent)}>{props.children}</div>
);

const CenterContentElement = (props: WithChildren): JSX.Element => (
  <div className={classNames(classes.CentralContent)}>{props.children}</div>
);

export const SettingsView = (): JSX.Element => {
  const letColRef = useRef<HTMLDivElement>(null);
  const { hasScrollbar: hasLeftScrollbar } = useDimensions(letColRef);
  const { state: topNavOffsetState } = useContext(TopNavContext);
  const { selectedOrgRole } = useUserRole();
  const { selectedOrganization } = useSelectedOrganization();
  const isOwner = selectedOrgRole === UserOrganizationRole.OWNER;
  const isAdmin = selectedOrgRole === UserOrganizationRole.ADMIN;

  const isInvoiceView = useMatch('/settings/invoice/:invoiceId');

  const topNavClasses = useTopNavClasses(topNavOffsetState);
  const leftColumnWrapperClass = classNames(classes.LeftSideWrapper, 'show-for-tablet-portrait', topNavClasses.vheight);
  const leftColumnClass = classNames(classes.LeftSideColumn, hasLeftScrollbar ? classes.LeftSideColumnScroller : null);

  if (isInvoiceView) {
    return (
      <>
        <Routes>
          <Route path={'invoice/:invoiceId'} element={<Invoice />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <div className={leftColumnWrapperClass}>
        <div className={leftColumnClass} ref={letColRef}>
          <SettingsMenu isLeftSideMenu={true} />
        </div>
      </div>

      <Routes>
        {/* TODO-Overhaul-Release - we'll need to expose these options for migrated members eventually. */}
        {selectedOrganization?.fromLegacy === false && (isOwner || isAdmin) && (
          <>
            <Route
              path={'workspaces'}
              element={
                <CenterContentElement>
                  <Workspaces />
                </CenterContentElement>
              }
            />
            <Route
              path={'team'}
              element={
                <CenterContentElement>
                  <TeamManagement />
                </CenterContentElement>
              }
            />
          </>
        )}
        {/* TODO-End */}
        {isOwner && (
          <>
            <Route
              path={'billings'}
              element={
                <CenterContentElement>
                  <BillingHistory />
                </CenterContentElement>
              }
            />
            <Route
              path={'business-details'}
              element={
                <IframeElement>
                  <BusinessDetails />
                </IframeElement>
              }
            />
            <Route
              path={'manage-subscription'}
              element={
                <IframeElement>
                  <Subscription />
                </IframeElement>
              }
            />
            <Route
              path={'affiliate'}
              element={
                <IframeElement>
                  <Affiliate />
                </IframeElement>
              }
            />
          </>
        )}

        <Route
          path={'account'}
          element={
            <CenterContentElement>
              <Account />
            </CenterContentElement>
          }
        />
        <Route
          path={'publishing'}
          element={
            <CenterContentElement>
              <Publishing />
            </CenterContentElement>
          }
        />
        <Route
          path={'profiles'}
          element={
            <CenterContentElement>
              <Profiles />
            </CenterContentElement>
          }
        />
        <Route
          path={'notifications'}
          element={
            <CenterContentElement>
              <EmailNotification />
            </CenterContentElement>
          }
        />
        <Route
          path={'tracking'}
          element={
            <CenterContentElement>
              <LinkTracking />
            </CenterContentElement>
          }
        />
        <Route
          path={'pause'}
          element={
            <CenterContentElement>
              <Pause />
            </CenterContentElement>
          }
        />
        <Route
          path={'extension'}
          element={
            <CenterContentElement>
              <BrowserExtension />
            </CenterContentElement>
          }
        />
        <Route
          path={'mobile'}
          element={
            <CenterContentElement>
              <MobileApp />
            </CenterContentElement>
          }
        />
        <Route
          path={'admin/*'}
          element={
            <CenterContentElement>
              <Admin />
            </CenterContentElement>
          }
        />
        <Route path={'*'} element={<NotFoundRedirect to={'account'} />} />
      </Routes>
    </>
  );
};
